import { GiftCardType } from "@/global/store/data/types";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { CgGift } from "react-icons/cg";
import { GiftCardAmounts, RecipientDetails } from "./giftCard.components";
import React, { ForwardedRef, MutableRefObject } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { EmptyState } from "@/shared/base/components/EmptyState/EmptyState";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { RecipientDetailsType, TempUserType } from "./usePurchaseGiftCard";
import { TmAccountDetails } from "../TmAccountDetails/TmAccountDetails";
import { useAppStore } from "@/global/store";
import { selectUser } from "@/global/store/data/user";
import { useTranslation } from "react-i18next";

interface SelectGiftCardProps<T = GiftCardType> {
  isGiftCardsLoading?: boolean;
  isAuthLoading?: boolean;
  giftCards: T[];
  selectedGiftCard: T | null;
  onSelectGiftCard: (a: T) => void;
  onClickCheckout: (a: any) => void;
  recipientForm: ReturnType<typeof useForm>;
  onSubmitRecipientForm: (a: any) => void;
  tempUser: TempUserType | null;
}

export const SelectGiftCard = React.forwardRef(
  (props: SelectGiftCardProps, recipientFormSubmitRef: any) => {
    // ---------------------------
    const { languages, giftCardConfigs } = useTeamConfig();
    const theme = useTheme();
    const { sm: smDown } = useCssMediaQuery("down");
    const user = useAppStore(selectUser);
    const customErrorMessage = giftCardConfigs?.customErrorMessage || "There are no gift cards available for purchase at this time, please check in later."; // prettier-ignore
    const hideRecipient = giftCardConfigs?.hideRecipient || false;
    const { t } = useTranslation();

    return (
      <Box
        border={`1px solid ${theme.palette.divider}`}
        borderRadius={0.9}
        bgcolor={theme.palette.background.paper}
      >
        <Stack px={3} py={2.5} justifyContent={"flex-end"}>
          <Typography
            variant="h6"
            fontWeight={"700"}
            lineHeight={1.3}
            color="text.primary"
          >
            {t("giftCard.select.cardTitle") ||
              languages?.purchaseGiftCard?.mainCardTitle}
          </Typography>
        </Stack>

        <Divider />

        {!props.isGiftCardsLoading && props.giftCards.length == 0 ? (
          <Stack
            direction={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            minHeight={smDown ? "300px" : "400px"}
            p={4}
          >
            <EmptyState
              description={
                t("giftCard.select.emptyStateDescription") ||
                languages?.purchaseGiftCard?.emptyStateDescription
              }
            />
          </Stack>
        ) : null}

        {props.isGiftCardsLoading || props.giftCards.length > 0 ? (
          <Box>
            <Box p={3} pt={2}>
              {user || props.tempUser ? (
                <Stack spacing={1} mb={2}>
                  <Typography
                    variant="h6"
                    color={"text.primary"}
                    fontWeight={700}
                  >
                    {t("accountDetails.title") || "Account Details"}
                  </Typography>
                  {user ? (
                    <TmAccountDetails
                      email={user.email}
                      accountId={user.accountId}
                    />
                  ) : null}
                  {props.tempUser ? (
                    <TmAccountDetails
                      email={props.tempUser.email}
                      accountId={props.tempUser.accountId}
                    />
                  ) : null}
                </Stack>
              ) : null}

              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={400}
                  color="text.secondary"
                >
                  {t("giftCard.select.cardDescription") ||
                    languages?.purchaseGiftCard?.mainCardDescription}
                </Typography>
              </Box>
              <Box mt={3}>
                <GiftCardAmounts
                  isLoading={props.isGiftCardsLoading}
                  giftCards={props.giftCards}
                  selectedGiftCard={props.selectedGiftCard}
                  onSelectGiftCard={props.onSelectGiftCard}
                />
              </Box>

              {/* <Box mt={3}>
                {!hideRecipient ? (
                  <RecipientDetails
                    ref={recipientFormSubmitRef}
                    formMethods={props.recipientForm}
                    onSubmit={props.onSubmitRecipientForm}
                  />
                ) : null}
              </Box> */}
            </Box>
            <Divider />
            <Stack direction={"row"} justifyContent={"flex-end"} p={3} py={2}>
              <Box width={!smDown ? "180px" : "100%"}>
                <ModalActionButton
                  isLoading={props.isAuthLoading}
                  variant="primary"
                  onClick={props.onClickCheckout}
                  disabled={!props.selectedGiftCard || props.isAuthLoading}
                  style={{ width: "100%", borderRadius: "50px" }}
                >
                  {t("giftCard.select.checkoutButtonText") || "Checkout"}
                </ModalActionButton>
              </Box>
            </Stack>
          </Box>
        ) : null}
      </Box>
    );
  }
);

SelectGiftCard.displayName = "SelectGiftCard";
